import { useCallback } from 'react'
import { useMailStore } from '../stores/mailStore'
import { useAccountStore } from '../stores/accountStore'
import type { Thread } from '../lib/types'

/**
 * Returns a `restoreThread(thread)` callback that moves a trashed thread
 * back to the Inbox via JMAP and removes it from local state (optimistic).
 */
export function useRestoreThread() {
  const { trashThread: removeFromList } = useMailStore()

  return useCallback((thread: Pick<Thread, 'id' | 'labels'>) => {
    // Optimistically remove from current (trash) view
    removeFromList(thread.id)

    const client = useAccountStore.getState().getActiveClient()
    if (!client) return

    // Get inbox mailbox ID directly from the account store (always up-to-date)
    const mailboxes = useAccountStore.getState().getActiveMailboxes()
    const inboxMb = client.findMailboxByRole(mailboxes, 'inbox')
    if (!inboxMb) return

    // Remove from trash and any other mailboxes the thread currently lives in
    const removeIds = thread.labels
      .map((l) => l.id)
      .filter((id) => !id.startsWith('virtual:') && id !== inboxMb.id)

    // Move to Inbox + unset $deleted so IMAP \Deleted flag is cleared
    client.moveThreadToMailbox(thread.id, inboxMb.id, removeIds, { '$deleted': null }).catch(() => {})
  }, [removeFromList])
}
